import React, { useContext, useState, useEffect, useRef, lazy, Suspense } from 'react';
import LeftPanelToolbar from './LeftPanelToolbar';
import { PanelContext } from '../../../app/providers/PanelContext';
import LeftPanelTickets from './left-panel-contents/LeftPanelTickets';
import ProfileContent from './left-panel-contents/ProfileContent';
import NewTicket from './left-panel-contents/NewTicket';
import SettingsContent from './left-panel-contents/SettingsContent';
import './LeftPanel.css';

const LazyLeftHomePanel = lazy(() => import('../../dashboard/LeftHomePanel'));

const DashboardFallback = () => (
  <div className="flex h-full w-full flex-col items-center justify-center" style={{ background: '#1a1a1a' }}>
    <div className="flex flex-col items-center gap-5">
      <div className="grid grid-cols-3 gap-2">
        {[0, 1, 2, 3, 4, 5].map(i => (
          <div
            key={i}
            className="rounded-lg animate-pulse"
            style={{
              width: 38,
              height: 26,
              background: 'rgba(255,255,255,0.06)',
              animationDelay: `${i * 120}ms`,
            }}
          />
        ))}
      </div>
      <div className="flex flex-col items-center gap-2">
        <div className="h-[2px] w-32 overflow-hidden rounded-full" style={{ background: 'rgba(255,255,255,0.08)' }}>
          <div
            className="h-full rounded-full"
            style={{
              width: '40%',
              background: 'rgba(99,130,255,0.5)',
              animation: 'dashboardBarSlide 1.5s ease-in-out infinite',
            }}
          />
        </div>
        <span className="text-[10px] font-medium tracking-widest text-slate-500">DASHBOARD</span>
      </div>
    </div>
    <style>{`
      @keyframes dashboardBarSlide {
        0% { transform: translateX(-100%); }
        50% { transform: translateX(250%); }
        100% { transform: translateX(-100%); }
      }
    `}</style>
  </div>
);

const LeftPanel = () => {
  const { selectedPanel } = useContext(PanelContext);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const dashboardMountedRef = useRef(false);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  if (selectedPanel === 'home') {
    dashboardMountedRef.current = true;
  }

  const toolbarWidth = windowWidth < 768 ? 45 : 60;

  return (
    <div className="flex h-full w-full overflow-hidden">
      <div
        className="toolbar-container"
        style={{
          width: `${toolbarWidth}px`,
          minWidth: `${toolbarWidth}px`,
        }}
      >
        <LeftPanelToolbar />
      </div>

      <div className="content-container relative flex-1 overflow-hidden">
        <div className="relative h-full w-full">
          <div
            className={`absolute inset-0 h-full w-full ${selectedPanel !== 'home' ? 'invisible pointer-events-none' : ''}`}
            aria-hidden={selectedPanel !== 'home'}
          >
            <Suspense fallback={<DashboardFallback />}>
              <LazyLeftHomePanel />
            </Suspense>
          </div>
          <div
            className={`absolute inset-0 h-full w-full ${selectedPanel !== 'flow' ? 'invisible pointer-events-none' : ''}`}
            aria-hidden={selectedPanel !== 'flow'}
          >
            <div className="flex h-full flex-1 flex-col bg-slate-100">
              <LeftPanelTickets />
            </div>
          </div>
          <div
            className={`absolute inset-0 h-full w-full ${selectedPanel !== 'profile' ? 'invisible pointer-events-none' : ''}`}
            aria-hidden={selectedPanel !== 'profile'}
          >
            <ProfileContent />
          </div>
          <div
            className={`absolute inset-0 h-full w-full ${selectedPanel !== 'newTicket' ? 'invisible pointer-events-none' : ''}`}
            aria-hidden={selectedPanel !== 'newTicket'}
          >
            <NewTicket />
          </div>
          <div
            className={`absolute inset-0 h-full w-full ${selectedPanel !== 'settings' ? 'invisible pointer-events-none' : ''}`}
            aria-hidden={selectedPanel !== 'settings'}
          >
            <SettingsContent />
          </div>
        </div>
      </div>
    </div>
  );
};

export default LeftPanel;
